// File Converter — drop any image (HEIC, JPG, WebP, BMP, GIF, etc.) and
// download a clean PNG. Runs entirely in the browser — no upload, no server
// load. Auto-applies EXIF rotation so portrait-mode iPhone photos don't come
// out sideways.
//
// Useful when the owner just needs a PNG of something quickly without going
// through Reel/Video Studio (e.g. share image for a text, work-detail upload,
// shop sign, etc).

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    ImagePlus,
    Loader2,
    Download,
    Trash2,
    X,
    Upload,
} from "lucide-react";

const ACCEPTED = /\.(png|jpe?g|webp|heic|heif|bmp|gif|tiff?|avif)$/i;
const ACCEPTED_MIME = "image/png,image/jpeg,image/webp,image/heic,image/heif,image/bmp,image/gif,image/tiff,image/avif,.heic,.heif";

function bytesToHuman(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Read a File into an HTMLImageElement, applying browser-native EXIF rotation
 * for JPEG via `createImageBitmap({imageOrientation: 'from-image'})` when
 * supported (Chrome/Edge/Firefox). For HEIC we route through heic2any first.
 * Returns a Promise<{ bitmap: ImageBitmap, width: number, height: number }>.
 */
async function loadAsBitmap(file) {
    const isHeic =
        /^image\/(heic|heif)/.test(file.type) || /\.hei[cf]$/i.test(file.name);
    let blob = file;
    if (isHeic) {
        const heic2any = (await import("heic2any")).default;
        const out = await heic2any({ blob: file, toType: "image/png", quality: 1 });
        blob = Array.isArray(out) ? out[0] : out;
    }
    // imageOrientation: 'from-image' makes the browser apply the EXIF rotation
    // automatically. Falls back gracefully on older browsers.
    return await createImageBitmap(blob, { imageOrientation: "from-image" });
}

async function convertFileToPng(file) {
    const bitmap = await loadAsBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close && bitmap.close();
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
            "image/png",
        );
    });
}

export default function FileConverter() {
    // entries: [{ id, name, src: File, status, error?, outBlob?, outUrl?, width?, height? }]
    const [entries, setEntries] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);

    // Revoke object URLs on unmount.
    useEffect(() => {
        return () => {
            entries.forEach((e) => {
                if (e.outUrl) URL.revokeObjectURL(e.outUrl);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enqueue = (files) => {
        const incoming = Array.from(files || []).filter((f) => {
            if (!ACCEPTED.test(f.name) && !/^image\//.test(f.type)) {
                toast.error(`${f.name}: not a recognized image format`);
                return false;
            }
            return true;
        });
        if (!incoming.length) return;
        const added = incoming.map((f) => ({
            id: crypto.randomUUID(),
            name: f.name,
            src: f,
            status: "queued",
        }));
        setEntries((prev) => [...prev, ...added]);
        // Kick off conversion asynchronously per file so big batches don't lock
        // the UI on the first slow HEIC.
        added.forEach(async (entry) => {
            setEntries((prev) =>
                prev.map((e) => (e.id === entry.id ? { ...e, status: "converting" } : e)),
            );
            try {
                const outBlob = await convertFileToPng(entry.src);
                const outUrl = URL.createObjectURL(outBlob);
                setEntries((prev) =>
                    prev.map((e) =>
                        e.id === entry.id
                            ? {
                                ...e,
                                status: "done",
                                outBlob,
                                outUrl,
                                outSize: outBlob.size,
                            }
                            : e,
                    ),
                );
            } catch (err) {
                console.error("convert failed", err);
                setEntries((prev) =>
                    prev.map((e) =>
                        e.id === entry.id
                            ? {
                                ...e,
                                status: "failed",
                                error: err?.message || "Conversion failed",
                            }
                            : e,
                    ),
                );
                toast.error(`${entry.name}: couldn't convert`);
            }
        });
    };

    const onPick = (e) => {
        enqueue(e.target.files);
        e.target.value = "";
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        enqueue(e.dataTransfer.files);
    };

    const removeEntry = (id) => {
        setEntries((prev) => {
            const ent = prev.find((e) => e.id === id);
            if (ent?.outUrl) URL.revokeObjectURL(ent.outUrl);
            return prev.filter((e) => e.id !== id);
        });
    };

    const clearAll = () => {
        entries.forEach((e) => e.outUrl && URL.revokeObjectURL(e.outUrl));
        setEntries([]);
    };

    const downloadAll = () => {
        const ready = entries.filter((e) => e.status === "done" && e.outUrl);
        if (!ready.length) {
            toast.error("Nothing to download yet.");
            return;
        }
        ready.forEach((e) => {
            const a = document.createElement("a");
            a.href = e.outUrl;
            a.download = e.name.replace(/\.[^.]+$/, "") + ".png";
            document.body.appendChild(a);
            a.click();
            a.remove();
        });
    };

    const doneCount = entries.filter((e) => e.status === "done").length;
    const busyCount = entries.filter((e) => e.status === "converting" || e.status === "queued").length;

    return (
        <div className="space-y-6 mt-6" data-testid="file-converter">
            <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h3 className="font-display font-black uppercase tracking-tight text-white text-xl">
                            File Converter → PNG
                        </h3>
                        <p className="text-xs text-zinc-400 mt-1">
                            Drop any image (HEIC, JPG, WebP, BMP, GIF, TIFF, AVIF) — get a clean PNG back. EXIF rotation is applied automatically so portrait iPhone photos come out the right way up. Runs 100% in your browser.
                        </p>
                    </div>
                    {entries.length > 0 && (
                        <div className="flex items-center gap-2">
                            {doneCount > 0 && (
                                <button
                                    type="button"
                                    onClick={downloadAll}
                                    data-testid="file-converter-download-all"
                                    className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-sm px-3 py-2 rounded-sm font-display font-bold uppercase tracking-wider"
                                >
                                    <Download className="w-4 h-4" /> Download all ({doneCount})
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={clearAll}
                                data-testid="file-converter-clear"
                                className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white text-zinc-300 text-xs px-3 py-2 rounded-sm"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                data-testid="file-converter-drop"
                className={`rounded-sm border-2 border-dashed p-10 sm:p-14 text-center cursor-pointer transition-colors ${
                    dragOver
                        ? "border-red-500 bg-red-600/10"
                        : "border-white/20 bg-zinc-950 hover:border-white/40"
                }`}
            >
                <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-3" />
                <p className="text-white font-display font-bold uppercase tracking-wider text-base">
                    Drop images here or click to pick
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                    HEIC · JPG · WebP · BMP · GIF · TIFF · AVIF → PNG
                </p>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPTED_MIME}
                    multiple
                    onChange={onPick}
                    className="hidden"
                    data-testid="file-converter-input"
                />
            </div>

            {/* Status bar */}
            {entries.length > 0 && (
                <div className="text-xs text-zinc-400 tabular-nums flex items-center gap-4">
                    <span>{entries.length} file{entries.length === 1 ? "" : "s"}</span>
                    <span className="text-emerald-400">{doneCount} done</span>
                    {busyCount > 0 && (
                        <span className="text-cyan-400 inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {busyCount} converting
                        </span>
                    )}
                </div>
            )}

            {/* Entries grid */}
            {entries.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {entries.map((e) => (
                        <div
                            key={e.id}
                            className="rounded-sm border border-white/10 bg-zinc-950 p-3 space-y-2"
                            data-testid={`file-converter-entry-${e.name}`}
                        >
                            <div className="flex items-start gap-2">
                                <div className="w-16 h-16 rounded-sm bg-zinc-900 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                                    {e.status === "done" && e.outUrl ? (
                                        <img src={e.outUrl} alt="" className="w-full h-full object-cover" />
                                    ) : e.status === "failed" ? (
                                        <X className="w-5 h-5 text-red-500" />
                                    ) : (
                                        <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs text-white truncate" title={e.name}>{e.name}</div>
                                    <div className="text-[10px] text-zinc-500">
                                        {e.status === "done"
                                            ? `${bytesToHuman(e.src.size)} → ${bytesToHuman(e.outSize)} PNG`
                                            : e.status === "failed"
                                                ? <span className="text-red-400">{e.error}</span>
                                                : "Converting…"}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeEntry(e.id)}
                                    className="text-zinc-500 hover:text-red-500 p-1"
                                    data-testid={`file-converter-remove-${e.name}`}
                                    aria-label="Remove"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {e.status === "done" && (
                                <a
                                    href={e.outUrl}
                                    download={e.name.replace(/\.[^.]+$/, "") + ".png"}
                                    className="inline-flex items-center justify-center gap-1.5 w-full text-[11px] font-bold uppercase tracking-wider border border-white/20 hover:border-white text-white px-3 py-1.5 rounded-sm"
                                    data-testid={`file-converter-download-${e.name}`}
                                >
                                    <Download className="w-3.5 h-3.5" /> Download PNG
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {entries.length === 0 && (
                <div className="text-center py-8">
                    <ImagePlus className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
                    <p className="text-xs text-zinc-500">Nothing converted yet.</p>
                </div>
            )}
        </div>
    );
}
